/**
 * LuckyWheel — animated prize wheel rendered to a 2D Canvas.
 *
 * v2 rewrite:
 *   - Big upright emoji per segment so prizes read at a glance (was tiny
 *     rotated text that required tilting the phone to read).
 *   - Rarity-driven gradient fills (legendary = gold, epic = purple, etc.)
 *     instead of static colors that didn't communicate value.
 *   - Pointer wiggles each time a segment passes underneath during a spin
 *     (mimics a physical wheel ticking against a peg).
 *   - Winning segment glows and the wheel as a whole keeps a subtle idle
 *     shimmer when at rest so the UI never reads as inert.
 *   - Spin curve uses a longer easeOutQuint so the final 15% is a slow
 *     creep that builds suspense.
 *
 * The wheel does NOT compute randomness — the backend resolves it via
 * VRF. We just animate the canvas to land on the right segment.
 */
import { useEffect, useRef, useState } from "react";
import type { WheelTier, SpinResult } from "@stellex-tycoon/shared-types";

type WheelPrize = WheelTier["prizes"][number];

export interface LuckyWheelProps {
  tier: WheelTier;
  /** Outcome from backend. When set with status=RESOLVED, wheel spins to it. */
  outcome?: SpinResult;
  /** Called when the spin animation completes. */
  onSpinComplete?: () => void;
  /** Width in px. Square aspect — height = width. */
  width?: number;
}

// Per-prize-type emoji icon. Always upright, single glyph — readable even
// at small wheel sizes.
function emojiFor(prize: WheelPrize): string {
  switch (prize.type) {
    case "CRYPTO":       return "🪙";
    case "MERCH":        return "👕";
    case "PERK":         return "⭐";
    case "FIAT_VOUCHER": return "💵";
    case "TOKEN_REFUND": return "🔄";
    case "NO_WIN":       return "💨";
    default:             return "🎁";
  }
}

// Two-stop gradient per rarity, drawn radially from inner edge to outer
// rim. rarityHint is lowercase in the public API. Winner override takes
// priority — winning segments always glow gold.
function rarityColors(rarity: string | undefined, isWinner: boolean): [string, string] {
  if (isWinner) return ["#ffd23f", "#f0b90b"];
  switch ((rarity || "").toLowerCase()) {
    case "legendary": return ["#7a5400", "#f0b90b"];
    case "rare":      return ["#3a1a5a", "#a855f7"];
    case "uncommon":  return ["#0a3a6a", "#3b82f6"];
    case "common":    return ["#1a2a35", "#2a3a4a"];
    default:          return ["#1a1a2e", "#2a2a3e"];
  }
}

// Short label under the emoji. NO_WIN shows nothing. Everything else
// extracts the first token of the name and clips to 6 chars — enough
// to disambiguate "1 TON" from "10 USDT" without overflowing the segment.
function shortLabel(prize: WheelPrize): string {
  if (prize.type === "NO_WIN") return "";
  const first = prize.name.split(/[\s·]/)[0];
  return first.length > 6 ? first.slice(0, 5) + "…" : first;
}

export function LuckyWheel({ tier, outcome, onSpinComplete, width = 320 }: LuckyWheelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const idleFrameRef = useRef<number | null>(null);
  const [spinAngle, setSpinAngle] = useState(0);
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [pointerWiggle, setPointerWiggle] = useState(0);
  const [idleShimmer, setIdleShimmer] = useState(0);
  const segmentCount = tier.prizes.length;
  const segmentArc = (Math.PI * 2) / Math.max(segmentCount, 1);
  const isSpinning = !!outcome && outcome.status === "RESOLVED" && highlightIndex === null;

  // ─── Static render — paints the current state into the canvas. ────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    canvas.width = width * dpr;
    canvas.height = width * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${width}px`;
    ctx.scale(dpr, dpr);

    const cx = width / 2;
    const cy = width / 2;
    const r = width / 2 - 10;

    ctx.clearRect(0, 0, width, width);

    // ── Outer ring with double border for depth ──────────────────────────
    ctx.beginPath();
    ctx.arc(cx, cy, r + 8, 0, Math.PI * 2);
    ctx.fillStyle = "#0a0a0a";
    ctx.fill();

    // Outer gold band — gradient gives a metallic look.
    const ringGrad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
    ringGrad.addColorStop(0, "#ffd23f");
    ringGrad.addColorStop(0.5, "#f0b90b");
    ringGrad.addColorStop(1, "#a07408");
    ctx.beginPath();
    ctx.arc(cx, cy, r + 5, 0, Math.PI * 2);
    ctx.strokeStyle = ringGrad;
    ctx.lineWidth = 5;
    ctx.stroke();

    // Inner thin dark border between ring and segments.
    ctx.beginPath();
    ctx.arc(cx, cy, r + 1, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,0,0,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // ── Segments ─────────────────────────────────────────────────────────
    tier.prizes.forEach((prize, i) => {
      const start = spinAngle + i * segmentArc;
      const end = start + segmentArc;
      const isWinner = i === highlightIndex;

      // Radial gradient: inner edge dark → rim lighter (the "lit from rim"
      // look on a real prize wheel).
      const [c0, c1] = rarityColors(prize.rarityHint, isWinner);
      const grad = ctx.createRadialGradient(cx, cy, r * 0.25, cx, cy, r);
      grad.addColorStop(0, c0);
      grad.addColorStop(1, c1);

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, end);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // Winner glow — extra translucent gold pass on top.
      if (isWinner) {
        const winGrad = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
        winGrad.addColorStop(0, "rgba(255,255,255,0)");
        winGrad.addColorStop(1, "rgba(255,210,63,0.55)");
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, start, end);
        ctx.closePath();
        ctx.fillStyle = winGrad;
        ctx.fill();
      }

      // Crisp gold divider line between segments.
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(start) * r, cy + Math.sin(start) * r);
      ctx.strokeStyle = "rgba(240,185,11,0.4)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // ── Label: big upright emoji + short text underneath ──────────────
      // Position at the segment's angular midpoint, radius 0.66 from center.
      const midAngle = start + segmentArc / 2;
      const labelR = r * 0.66;
      const lx = cx + Math.cos(midAngle) * labelR;
      const ly = cy + Math.sin(midAngle) * labelR;

      const emojiSize = Math.max(14, Math.round(width / 14));
      const labelSize = Math.max(8, Math.round(width / 32));

      ctx.save();
      ctx.translate(lx, ly);
      // Keep glyphs UPRIGHT — no rotation. They're readable from one
      // viewing angle (head-on) which is how the user actually looks at
      // a phone screen. Past wheels rotated the text radially and ended
      // up upside-down on the bottom half.
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.font = `${emojiSize}px -apple-system, system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
      ctx.fillText(emojiFor(prize), 0, -labelSize * 0.4);

      const label = shortLabel(prize);
      if (label) {
        ctx.font = `700 ${labelSize}px -apple-system, system-ui, sans-serif`;
        ctx.fillStyle = isWinner ? "#1a1208" : "#ffffff";
        // 1px shadow so white text stays legible on lighter rarity gradients.
        ctx.shadowColor = "rgba(0,0,0,0.7)";
        ctx.shadowBlur = 2;
        ctx.fillText(label, 0, emojiSize * 0.55);
        ctx.shadowBlur = 0;
      }
      ctx.restore();
    });

    // ── Hub (covers segment vertices for a clean center) ─────────────────
    const hubR = Math.max(22, width * 0.09);
    ctx.beginPath();
    ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
    ctx.fillStyle = "#0a0a0a";
    ctx.fill();
    ctx.strokeStyle = ringGrad;
    ctx.lineWidth = 3;
    ctx.stroke();

    // "STELLEX" text in the hub
    ctx.fillStyle = "#f0b90b";
    ctx.font = `900 ${Math.max(7, Math.round(width / 38))}px -apple-system, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("STELLEX", cx, cy);

    // ── Idle shimmer — a soft white sweep that orbits the rim when the
    // wheel isn't spinning. Keeps the surface "alive" between spins.
    if (!isSpinning) {
      const sweepAngle = idleShimmer;
      const sweepGrad = ctx.createRadialGradient(
        cx + Math.cos(sweepAngle) * r * 0.85,
        cy + Math.sin(sweepAngle) * r * 0.85,
        2,
        cx + Math.cos(sweepAngle) * r * 0.85,
        cy + Math.sin(sweepAngle) * r * 0.85,
        r * 0.45
      );
      sweepGrad.addColorStop(0, "rgba(255,255,255,0.12)");
      sweepGrad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = sweepGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Pointer (top, with wiggle while spinning) ────────────────────────
    const pointerX = cx + pointerWiggle * 4;
    ctx.save();
    ctx.shadowColor = "rgba(240,185,11,0.7)";
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(pointerX - 12, 2);
    ctx.lineTo(pointerX + 12, 2);
    ctx.lineTo(pointerX, 32);
    ctx.closePath();
    const pointerGrad = ctx.createLinearGradient(pointerX - 12, 0, pointerX + 12, 30);
    pointerGrad.addColorStop(0, "#ffd23f");
    pointerGrad.addColorStop(1, "#bf8a08");
    ctx.fillStyle = pointerGrad;
    ctx.fill();
    ctx.strokeStyle = "#0a0a0a";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }, [tier, width, highlightIndex, spinAngle, segmentArc, pointerWiggle, idleShimmer, isSpinning]);

  // ─── Idle shimmer driver — slow orbit at 1 rad / 2s ───────────────────
  useEffect(() => {
    if (isSpinning) return;
    let last = performance.now();
    const tick = (t: number) => {
      const dt = (t - last) / 1000;
      last = t;
      setIdleShimmer((a) => (a + dt * 0.5) % (Math.PI * 2));
      idleFrameRef.current = requestAnimationFrame(tick);
    };
    idleFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (idleFrameRef.current !== null) cancelAnimationFrame(idleFrameRef.current);
    };
  }, [isSpinning]);

  // ─── Spin animation — drives the wheel to land on the winning segment.
  useEffect(() => {
    if (!outcome || outcome.status !== "RESOLVED") return;
    if (segmentCount === 0) return;

    const winIndex = outcome.prize
      ? tier.prizes.findIndex((p) => p.prizeId === outcome.prize?.id)
      : tier.prizes.findIndex((p) => p.type === "NO_WIN");
    const targetIndex = winIndex >= 0 ? winIndex : 0;

    // Pointer at angle -π/2 (top). We want segment[targetIndex]'s midpoint
    // under it: spinAngle + (targetIndex + 0.5) * segmentArc ≡ -π/2.
    // Add SPINS full rotations for drama.
    const SPINS = 6;
    const finalAngle =
      -Math.PI / 2 - (targetIndex + 0.5) * segmentArc + SPINS * Math.PI * 2;

    const startAngle = spinAngle;
    const startTime = performance.now();
    const DURATION = 5000; // longer than v1 (was 4200) for more suspense

    let lastTickedSegment = -1;

    const tick = (t: number) => {
      const elapsed = t - startTime;
      const p = Math.min(elapsed / DURATION, 1);
      // easeOutQuint — sharper deceleration at the end vs the old cubic.
      const eased = 1 - Math.pow(1 - p, 5);
      const a = startAngle + (finalAngle - startAngle) * eased;
      setSpinAngle(a);

      // Pointer wiggle on segment crossing — a real wheel ticks each time
      // a peg passes under the pointer.
      const currentSegment = Math.floor(
        ((-Math.PI / 2 - a) / segmentArc) % segmentCount
      );
      if (currentSegment !== lastTickedSegment) {
        lastTickedSegment = currentSegment;
        // Wiggle amplitude shrinks as we slow down.
        const amp = (1 - eased) * 0.6 + 0.05;
        setPointerWiggle(amp);
        // Decay back to 0 over the next few frames (handled by the next
        // tick that doesn't see a segment crossing).
        window.setTimeout(() => setPointerWiggle(0), 80);
      }

      if (p < 1) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        setPointerWiggle(0);
        setHighlightIndex(targetIndex);
        // Brief delay so the highlight pulse is visible before the
        // outcome-card overlay shows up.
        window.setTimeout(() => onSpinComplete?.(), 300);
      }
    };
    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome?.spinId]);

  // Reset highlight when the user starts a fresh spin (new outcome arrives).
  useEffect(() => {
    if (outcome?.status === "RESOLVED") return;
    setHighlightIndex(null);
  }, [outcome?.spinId]);

  return (
    <div
      style={{
        position: "relative",
        width,
        height: width,
        margin: "0 auto",
        // Outer glow that intensifies during/after spin to draw the eye.
        filter:
          highlightIndex !== null
            ? "drop-shadow(0 0 24px rgba(240,185,11,0.45))"
            : "drop-shadow(0 0 12px rgba(240,185,11,0.15))",
        transition: "filter 0.4s",
      }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={width}
        style={{ display: "block", touchAction: "none" }}
      />
    </div>
  );
}
